/**
 * PublicScenariosIndex: the public /demo-business/scenarios landing page.
 *
 * Audience: rental-business owners exploring ConfirmPH by themselves.
 * Purpose: let them try the same interactive scenarios promoters demo,
 * but without a single line of sales-agent guidance.  All content is
 * product-truth and business-owner facing.
 *
 * Reuses SCENARIOS + CATEGORIES from /pages/promoter/demos/scenarios.js
 * so we never duplicate scenario definitions.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, ArrowRight, Search, X, ChevronRight,
} from "lucide-react";
import { SCENARIOS } from "../../pages/promoter/demos/scenarios";

// Product-truth category framings for the public catalog.  These are the
// exact labels rental-business owners see, tuned for their audience, and
// intentionally different from the promoter-facing category chrome.
const PUBLIC_CATEGORIES = [
  { key: "intake",    label: "Getting a Booking",
    subtitle: "See how inquiries from Messenger, WhatsApp, SMS, phone calls and walk-ins become organised bookings." },
  { key: "problems",  label: "Booking Issues",
    subtitle: "See how ConfirmPH surfaces overlapping bookings, tight scheduling and pending confirmations." },
  { key: "vehicle",   label: "Vehicle Issues",
    subtitle: "See how vehicle problems can be linked to the upcoming bookings that may be affected." },
  { key: "staff",     label: "Daily Operations",
    subtitle: "See reminders, follow-ups and the activity history that keeps everyone on the same page." },
  { key: "financial", label: "Financial Records",
    subtitle: "See how deposits, balances and payment notes are kept on the booking record." },
  { key: "trust",     label: "Trust & Reviews",
    subtitle: "See booking-linked reviews and the customer-facing profile every ConfirmPH business gets." },
  { key: "presence",  label: "Online Presence",
    subtitle: "See the customer-facing Business Page and the full ConfirmPH Website." },
];

// One-line owner-facing descriptions for the scenario cards.  If a scenario
// doesn't have an override here we fall back to its `subtitle`, which was
// already product-truth wording.
const PUBLIC_ONELINERS = {
  "messenger-inquiry":       "See how a Messenger inquiry becomes a structured booking record.",
  "whatsapp-inquiry":        "See how a WhatsApp inquiry lands inside ConfirmPH without retyping.",
  "sms-inquiry":             "See how an SMS inquiry becomes a booking with the same flow.",
  "phone-call":              "See how details taken during a phone call become a booking.",
  "walk-in":                 "See how a walk-in customer at the counter is recorded and confirmed.",
  "double-booking":          "See how ConfirmPH warns when two recorded bookings overlap for the same vehicle.",
  "tight-buffer":            "See how tight turnaround times can be identified before pickup day.",
  "date-change":             "See how a customer's date change is recorded on the same booking.",
  "awaiting-confirmation":   "See how pending customer confirmations stay visible until they respond.",
  "forgotten-booking":       "See why recording inquiries as they arrive keeps them from being forgotten.",
  "vehicle-breakdown":       "See how a vehicle issue can be linked to the upcoming bookings it may affect.",
  "maintenance":             "See how scheduled maintenance blocks out a vehicle from future bookings.",
  "late-return":             "See how an overdue return can be surfaced against the next booking.",
  "replacement":             "See how a vehicle can be swapped on a booking without losing the record.",
  "damage-incident":         "See how a damage report is logged against the specific vehicle and booking.",
  "follow-up":               "See how a follow-up reminder keeps a pending booking on your radar.",
  "multi-staff":             "See how activity history shows who did what and when.",
  "upcoming-pickup":         "See how today's and tomorrow's pickups are highlighted for the team.",
  "deposit-balance":         "See how deposit, amount paid and remaining balance are kept on the booking.",
  "verified-review":         "See how reviews tied to completed bookings can build customer trust.",
  "business-page":           "See what a customer sees on your public ConfirmPH Business Page.",
  "public-website":          "See the multi-page ConfirmPH Website your business can have.",
};

export default function PublicScenariosIndex() {
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState(null);

  const grouped = useMemo(() => {
    const query = q.trim().toLowerCase();
    const map = new Map(PUBLIC_CATEGORIES.map((c) => [c.key, []]));
    for (const s of SCENARIOS) {
      const hay = [s.id, s.title, s.subtitle, ...(s.keywords || [])].join(" ").toLowerCase();
      if (query && !hay.includes(query)) continue;
      if (activeCat && s.category !== activeCat) continue;
      if (map.has(s.category)) map.get(s.category).push(s);
      else {
        // Unknown categories (kept for completeness) fall into the last group.
        if (!map.has("presence")) map.set("presence", []);
        map.get("presence").push(s);
      }
    }
    return map;
  }, [q, activeCat]);

  const totalMatches = Array.from(grouped.values()).reduce((a, arr) => a + arr.length, 0);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <PublicHeader />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        {/* Hero */}
        <div className="max-w-3xl">
          <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-800 font-semibold inline-flex items-center gap-1">
            <ShieldCheck className="w-3.5 h-3.5" /> DEMO · nothing you do here is real
          </div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold text-slate-900 leading-tight">
            See ConfirmPH in action
          </h1>
          <p className="mt-2 text-sm sm:text-base text-slate-700">
            Explore common rental situations and see how ConfirmPH handles them step by step, without signing up.
          </p>
        </div>

        {/* Search + filter */}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="search" value={q} onChange={(e) => setQ(e.target.value)}
                    placeholder="Search: messenger, double booking, damage, deposit…"
                    data-testid="public-scenarios-search"
                    className="w-full rounded-full border border-slate-300 pl-9 pr-9 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
            {q && (
              <button onClick={() => setQ("")} data-testid="public-scenarios-clear-search"
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-slate-100 text-slate-500">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <CategoryFilter active={activeCat} setActive={setActiveCat} />
        </div>

        {/* Grouped scenario grid */}
        <div className="mt-8 space-y-8">
          {PUBLIC_CATEGORIES.map((cat) => {
            const items = grouped.get(cat.key) || [];
            if (items.length === 0) return null;
            return (
              <section key={cat.key} data-testid={`public-scenarios-cat-${cat.key}`}>
                <div className="flex items-baseline gap-3 mb-3">
                  <h2 className="font-display text-lg font-semibold text-slate-900">{cat.label}</h2>
                  <span className="text-[11px] text-slate-500">{cat.subtitle}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {items.map((s) => <ScenarioCard key={s.id} s={s} />)}
                </div>
              </section>
            );
          })}
          {totalMatches === 0 && (
            <div className="text-center py-14 text-slate-500 text-sm" data-testid="public-scenarios-empty">
              No scenarios match &ldquo;{q}&rdquo;. Try a different keyword or clear filters.
            </div>
          )}
        </div>

        {/* Bottom CTA */}
        <PublicBottomCta />
      </main>

      <PublicFooter />
    </div>
  );
}

function ScenarioCard({ s }) {
  const oneLiner = PUBLIC_ONELINERS[s.id] || s.subtitle;
  const cat = PUBLIC_CATEGORIES.find((c) => c.key === s.category);
  return (
    <Link to={`/demo-business/scenarios/${s.id}`}
          data-testid={`public-scenario-card-${s.id}`}
          className="group rounded-xl border border-slate-200 bg-white hover:border-emerald-400 hover:shadow-sm p-4 flex flex-col transition-all">
      {cat && (
        <div className="text-[9px] uppercase tracking-[0.2em] font-semibold text-emerald-700">
          {cat.label}
        </div>
      )}
      <div className="mt-1 font-display font-semibold text-slate-900 leading-tight">{s.title}</div>
      <p className="mt-1.5 text-xs text-slate-600 leading-relaxed flex-1">{oneLiner}</p>
      <div className="mt-3 flex items-center justify-between">
        <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
          Phone · Desktop
        </div>
        <div className="inline-flex items-center gap-1 text-xs text-emerald-800 font-semibold group-hover:gap-2 transition-all">
          Start scenario <ArrowRight className="w-3.5 h-3.5" />
        </div>
      </div>
    </Link>
  );
}

function CategoryFilter({ active, setActive }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="public-scenarios-category-filter">
      <FilterChip active={!active} onClick={() => setActive(null)}>All</FilterChip>
      {PUBLIC_CATEGORIES.map((c) => (
        <FilterChip key={c.key} active={active === c.key} onClick={() => setActive(c.key)}
                     testId={`public-scenarios-cat-chip-${c.key}`}>
          {c.label}
        </FilterChip>
      ))}
    </div>
  );
}

function FilterChip({ active, onClick, children, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              active ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
            }`}>
      {children}
    </button>
  );
}

function PublicHeader() {
  return (
    <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
        <Link to="/" className="flex items-center gap-2" data-testid="public-scenarios-back-home">
          <img src="/brand/confirmph-logo.png" alt="ConfirmPH" className="h-7 w-auto"
                onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <span className="font-display font-semibold text-slate-900">ConfirmPH</span>
        </Link>
        <span className="text-[10px] uppercase tracking-[0.2em] text-rose-700 font-bold border border-rose-200 bg-rose-50 rounded-full px-2 py-0.5">
          Demo
        </span>
        <div className="flex-1" />
        <Link to="/register" data-testid="public-scenarios-signup"
              className="text-xs px-3 py-1.5 rounded-full bg-[#1B4332] text-white font-medium">
          Start free
        </Link>
      </div>
    </header>
  );
}

function PublicBottomCta() {
  return (
    <div className="mt-12 rounded-2xl bg-gradient-to-br from-emerald-900 to-slate-900 text-white p-6 sm:p-8"
         data-testid="public-scenarios-bottom-cta">
      <div className="max-w-2xl">
        <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-300 font-semibold">
          Ready when you are
        </div>
        <h3 className="mt-1 font-display text-2xl sm:text-3xl font-semibold leading-tight">
          Want to try ConfirmPH for your own rental business?
        </h3>
        <p className="mt-2 text-sm text-emerald-100">
          It&apos;s free to start. Bring your fleet, invite your staff, and keep every booking in one place.
        </p>
        <div className="mt-5 flex items-center gap-2 flex-wrap">
          <Link to="/register" data-testid="public-scenarios-cta-start-free"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white text-emerald-900 text-sm font-semibold">
            Start free <ChevronRight className="w-4 h-4" />
          </Link>
          <Link to="/pricing" data-testid="public-scenarios-cta-view-plans"
                className="text-sm text-emerald-100 underline underline-offset-2">
            View plans
          </Link>
        </div>
      </div>
    </div>
  );
}

function PublicFooter() {
  return (
    <footer className="mt-16 border-t border-slate-200 py-6 text-center text-xs text-slate-500">
      This is a demo. Nothing shown is a real booking, customer, or vehicle. ©{new Date().getFullYear()} ConfirmPH.
    </footer>
  );
}
